import React, { Fragment, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import styles from './styles.scss';
import ReactInfiniteScroller from 'react-infinite-scroller';
import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  AppBarContext,
  Button,
  Checkbox,
  getNextSortDirection,
  Icons,
} from '@densityco/ui';
import ListView, { ListViewColumn, ListViewColumnSpacer } from '../ListView';
import StatusTag from '../StatusTag';
import Modal from '../Modal';
import HorizontalForm from '../HorizontalForm';
import Tooltip from '../Tooltip';
import DataCaptureStatusTag from '../DataCaptureStatusTag';
import { NOTE_TYPES } from '../Note';
import SetupStepInfo from '../SetupStepInfo';

import COLUMN_SETS from './columnSets';

import colorVariables from '@densityco/ui/variables/colors.json';

import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import singleClickDetector from '../../utils/single-click-detector';
import { openInNewTab } from '../../utils/navigation';
import { deserializeListViewState } from '../../utils/rx-store-serialization';
import { ON_PREM } from '../../utils/environment';
import { isDateStringValid } from '../../utils/time';

import ActionBar, {
  FilterOperatorInputPercent,
  FilterOperatorInputBoolean,
  FilterOperatorInputChoices,
  FilterOperatorInputMultiChoices,
  FilterOperatorInputText,
  FilterOperatorInputDate,
} from '../ActionBar';
import { FilterDefinition, FilterOperator } from '../../types/filters';
import { Sort, SortColumn, SortDirection } from '../../types/sort';
import { ALL_ORGANIZATIONS, deserializeOrganization } from '../../types/all-organizations';
import ColoredPercent from '../ColoredPercent';
import ColoredQaedEventCount from '../ColoredQaedEventCount';
import ColoredOSVersion from '../ColoredOSVersion';
import SpaceNameAncestry from '../SpaceNameAncestry';
import Loading from '../Loading';
import ErrorState from '../ErrorState';
import CaptureScheduleModal from '../CaptureScheduleModal';
import ListViewTwoRowHeader from '../ListViewTwoRowHeader';
import DetailsButton from '../DetailsButton';
import SensorActionButtons from '../Sensors/ActionButtons';

import { Choice } from '../../types/choice';
import InConfigurationStore from '../../rx-stores/in-configuration';
import { InConfigurationActionTypes } from '../../types/in-configuration';
import {
  Sensor,
  SensorType,
  CaptureStatus,
  CAPTURE_STATUS_TO_PRETTY_NAME,
  ASSIGNABLE_TEAM_TO_PRETTY_NAME,
} from '../../types/api';
import { ResourceStatus } from '../../types/resource';

const MDASH = <Fragment>&mdash;</Fragment>;

function formatDate(timestamp: string) {
  return moment(timestamp).format('MM/DD/YYYY');
}

const SENSOR_STATUS_CHOICES = [
  { id: 'online', label: 'Online' },
  { id: 'unconfigured', label: 'Unconfigured' },
  { id: 'error', label: 'Error' },
  { id: 'low_power', label: 'Low Power' },
  { id: 'archived', label: 'Archived' },
];

export const InConfiguration: React.FunctionComponent<{}> = () => {
  const state = useRxStore(InConfigurationStore);
  const dispatch = useRxDispatch();
  const location = useLocation();
  const { organizationIdOrAll } = useParams();

  let scrollParentRef = useRef<HTMLDivElement>(null);

  const [captureScheduleModalOpen, setCaptureScheduleModalOpen] = useState(
    false,
  );

  useEffect(() => {
    const { filters, sort, searchText } = deserializeListViewState(
      location.search.replace(/^\?/, ''),
    );

    dispatch({
      type: InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_PAGE,
      filters,
      sort,
      searchText,
      organizationId: deserializeOrganization(organizationIdOrAll),
    });
  }, [organizationIdOrAll]);
  // remove accuracy filter for on prem
  const setupStepChoices = state.resource.status === ResourceStatus.COMPLETE ? (
    Array.from(new Set(Object.values(state.resource.data.sensorStateHumanReadableNameMapping))).map(v => ({id: v, label: v}))
  ) : [];
  const filterDefinitions = [
    {
      id: 'serial_number',
      label: 'Serial Number',
      operators: [
        {
          id: 'equals',
          label: 'is equal to',
          allowMultiple: false,
          isValueValid: s => typeof s === 'string',
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
        {
          id: 'startswith',
          label: 'starts with',
          allowMultiple: false,
          isValueValid: s => typeof s === 'string',
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
        {
          id: 'kind',
          label: 'is of kind',
          allowMultiple: true,
          isValueValid: s => s.length > 0,
          initialValue: [],
          component: (props) => (
            <FilterOperatorInputMultiChoices
              choices={[
                {id: 'entry', label: 'Entry'},
                {id: 'openarea', label: 'Open Area'},
              ]}
              {...props}
            />
          ),
        } as FilterOperator<Array<string>>,
        {
          id: 'hardware_model',
          label: 'is of hardware model',
          allowMultiple: true,
          isValueValid: s => s.length > 0,
          initialValue: [],
          component: (props) => (
            <FilterOperatorInputMultiChoices
              choices={[
                {id: 's5', label: 'S5'},
                {id: 's5b', label: 'S5B'},
                {id: 'oa1', label: 'OA1'},
                {id: 'r60', label: 'R60'},
                {id: 'r56', label: 'R56'},
              ]}
              {...props}
            />
          ),
        } as FilterOperator<Array<string>>,
        {
          id: 'is_virtual',
          label: 'is virtual',
          allowMultiple: true,
          isValueValid: s => s !== null,
          initialValue: null,
          component: FilterOperatorInputBoolean,
        } as FilterOperator<boolean>,
      ]
    },
    {
      id: 'total_accuracy',
      label: 'Total Accuracy',
      operators: [
        {
          id: 'lt',
          label: 'Less Than',
          allowMultiple: false,
          isValueValid: s => !isNaN(parseFloat(s)),
          initialValue: '',
          component: FilterOperatorInputPercent,
        } as FilterOperator<string>,
        {
          id: 'gt',
          label: 'Greater Than',
          allowMultiple: false,
          isValueValid: s => !isNaN(parseFloat(s)),
          initialValue: '',
          component: FilterOperatorInputPercent,
        } as FilterOperator<string>,
      ],
    },
    {
      id: 'in_mpi',
      label: 'In MPI',
      operators: [
        {
          id: 'equals',
          label: 'is equal to',
          allowMultiple: false,
          isValueValid: s => typeof s === 'boolean',
          initialValue: null,
          component: FilterOperatorInputBoolean,
        } as FilterOperator<boolean>,
      ],
    },
    {
      id: 'current_state',
      label: 'Current State',
      operators: [
        {
          id: 'equals',
          label: 'is equal to',
          allowMultiple: true,
          isValueValid: s => typeof s === 'string',
          initialValue: '',
          component: (props) => state.resource.status === ResourceStatus.COMPLETE ?
            <FilterOperatorInputChoices
              choices={state.resource.data.sensorStateChoices}
              {...props}
            /> : null,
        } as FilterOperator<string>,
        {
          id: 'requires_human_interaction',
          label: 'requires human interaction',
          allowMultiple: false,
          isValueValid: s => typeof s === 'boolean',
          initialValue: null,
          component: FilterOperatorInputBoolean,
        } as FilterOperator<boolean>,
      ],
    },
    {
      id: 'human_readable_state_name',
      label: 'Setup Step',
      operators: [
        {
          id: 'in',
          label: 'is one of',
          allowMultiple: true,
          isValueValid: state => state.every(i => setupStepChoices.map(s => s.id).includes(i)),
          initialValue: [],
          component: (props) => state.resource.status === ResourceStatus.COMPLETE ? (
            <FilterOperatorInputMultiChoices choices={setupStepChoices} {...props} />
          ) : null,
        } as FilterOperator<Array<string>>,
        {
          id: 'requires_human_interaction',
          label: 'requires human interaction',
          allowMultiple: false,
          isValueValid: s => typeof s === 'boolean',
          initialValue: null,
          component: FilterOperatorInputBoolean,
        } as FilterOperator<boolean>,
      ],
    },
    {
      id: 'assigned_team',
      label: 'Assigned Team',
      operators: [
        {
          id: 'equals',
          label: 'is equal to',
          allowMultiple: false,
          isValueValid: s => true,
          initialValue: [],
          component: (props) =>
            <FilterOperatorInputMultiChoices
              choices={
                Object.entries(ASSIGNABLE_TEAM_TO_PRETTY_NAME)
                  .map(([id, label]) => ({ id, label }))
              }
              {...props}
            />,
        } as FilterOperator<Array<string>>,
      ],
    },
    {
      id: 'statuses',
      label: 'Status',
      operators: [
        {
          id: 'in',
          label: 'is one of',
          allowMultiple: true,
          isValueValid: state => state.every(i => SENSOR_STATUS_CHOICES.map(s => s.id).includes(i)),
          initialValue: [],
          component: props => (
            <FilterOperatorInputMultiChoices
              choices={SENSOR_STATUS_CHOICES}
              {...props}
            />
          ),
        } as FilterOperator<Array<Choice['id']>>,
      ],
    },
    {
      id: 'first_online',
      label: 'First Online',
      operators: [
        {
          id: 'gt',
          label: 'is after',
          allowMultiple: false,
          isValueValid: s => isDateStringValid(s),
          initialValue: '',
          component: FilterOperatorInputDate,
        } as FilterOperator<string>,
        {
          id: 'lt',
          label: 'is before',
          allowMultiple: false,
          isValueValid: s => isDateStringValid(s),
          initialValue: '',
          component: FilterOperatorInputDate,
        } as FilterOperator<string>,
      ]
    },
    {
      id: 'qaed_events',
      label: "QA'd Events",
      operators: [
        {
          id: 'gte',
          label: 'is greater than or equal to',
          allowMultiple: false,
          isValueValid: s => !isNaN(Number(s)),
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
        {
          id: 'lte',
          label: 'is less than or equal to',
          allowMultiple: false,
          isValueValid: s => !isNaN(Number(s)),
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
      ]
    },
    {
      id: 'os_version',
      label: "OS Version",
      operators: [
        {
          id: 'equals',
          label: 'starts with or is equal to',
          allowMultiple: false,
          isValueValid: s => typeof s === 'string',
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
      ]
    },
    {
      id: 'annotated_events',
      label: "Annotated Events",
      operators: [
        {
          id: 'gte',
          label: 'is greater than or equal to',
          allowMultiple: false,
          isValueValid: s => !isNaN(Number(s)),
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
        {
          id: 'lte',
          label: 'is less than or equal to',
          allowMultiple: false,
          isValueValid: s => !isNaN(Number(s)),
          initialValue: '',
          component: FilterOperatorInputText,
        } as FilterOperator<string>,
      ]
    }
  ].filter(fd => !(ON_PREM && fd.id === 'total_accuracy'));

  const header = (
    <Fragment>
      <ActionBar
        units="Sensors"
        totalItemCount={
          state.resource.status === ResourceStatus.COMPLETE
            ? state.resource.data.total
            : null
        }
        selectedItemCount={state.selectedSensorSerialNumbers.length}
        onClearSelectedItems={() =>
          dispatch({
            type: InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_CHECKS,
            serialNumbers: state.selectedSensorSerialNumbers,
            checked: false,
          })
        }
        selectedActionButtons={
          <SensorActionButtons
            presentation="list"
            showMPICreationButton
            sensors={
              state.resource.status === ResourceStatus.COMPLETE
                ? state.resource.data.sensors.filter(i =>
                  state.selectedSensorSerialNumbers.includes(i.serial_number),
                )
                : []
            }
          />
        }
        showSearch
        searchPlaceholder="Search by doorway name, id, or serial"
        searchWidth={375}
        searchValue={state.searchText}
        onChangeSearch={e =>
          dispatch({
            type: InConfigurationActionTypes.IN_CONFIGURATION_SET_SEARCH_TEXT,
            text: e.target.value,
          })
        }
        showFilters
        filterDefinitions={filterDefinitions}
        filters={state.filters}
        onChangeFilters={filters =>
          dispatch({
            type: InConfigurationActionTypes.IN_CONFIGURATION_SET_FILTERS,
            filters,
          })
        }
        actionButtons={
          <Button
            leftIcon={<Icons.Chart1 />}
            onClick={() => {
              dispatch({
                type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT,
                organizationId: deserializeOrganization(organizationIdOrAll),
              });
            }}
            data-test-id="in-configuration-export-csv"
          >
            Export As CSV
          </Button>
        }
      />
    </Fragment>
  );

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
      return <p>Idle</p>;
    case ResourceStatus.LOADING:
      return (
        <div className={styles.sensorsContainer}>
          {header}
          <Loading />
        </div>
      );
    case ResourceStatus.ERROR:
      return (
        <div className={styles.sensorsContainer}>
          {header}
          <ErrorState error={state.resource.error} />
        </div>
      );
    case ResourceStatus.COMPLETE:
      const resourceContent = state.resource.data;
      let visibleData = resourceContent.sensors;
      const columnSet = COLUMN_SETS.normal({
        allOrganizations: organizationIdOrAll === ALL_ORGANIZATIONS,
        onPrem: ON_PREM,
      });

      return (
        <Fragment>
          <Modal
            visible={state.resource.data.csvExport.status !== 'IDLE'}
            onBlur={() =>
              dispatch({
                type:
                  InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL,
              })
            }
            onEscape={() =>
              dispatch({
                type:
                  InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL,
              })
            }
          >
            <AppBar>
              <AppBarTitle>Generating CSV</AppBarTitle>
            </AppBar>
            <div
              className={styles.csvExportModalWrapper}
              data-test-id="in-configuration-export-csv-modal"
            >
              {state.resource.data.csvExport.status === 'LOADING_TOTAL' ? (
                <p data-test-id="in-configuration-export-csv-modal-loading-text">Loading data...</p>
              ) : null}
              {state.resource.data.csvExport.status === 'LOADING_DATA' ? (
                <div
                  className={styles.csvExportModalProgressBarOuter}
                  data-test-id="in-configuration-export-csv-modal-loading-progress-bar"
                >
                  <div
                    className={styles.csvExportModalProgressBarInner}
                    style={{
                      width: `${(state.resource.data.csvExport.loadedRows /
                        state.resource.data.csvExport.totalRows) *
                        100}%`,
                    }}
                  />
                </div>
              ) : null}
              {state.resource.data.csvExport.status === 'ERROR' ? (
                <ErrorState error={state.resource.data.csvExport.error} />
              ) : null}
            </div>
            <AppBarContext.Provider value="BOTTOM_ACTIONS">
              <AppBar>
                <AppBarSection />
                <AppBarSection>
                  <Button
                    variant="underline"
                    onClick={() =>
                      dispatch({
                        type:
                          InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL,
                      })
                    }
                  >
                    Cancel
                  </Button>
                </AppBarSection>
              </AppBar>
            </AppBarContext.Provider>
          </Modal>

          {header}

          <div
            className={styles.sensorsList}
            ref={scrollParentRef}
            data-test-id="in-configuration-wrapper"
          >
            <ReactInfiniteScroller
              pageStart={1}
              loadMore={page =>
                dispatch({
                  type: InConfigurationActionTypes.IN_CONFIGURATION_SET_PAGE,
                  value: page,
                })
              }
              hasMore={
                state.resource.data.total >
                state.resource.data.sensors.length &&
                state.resource.data.fetchingMoreSensors.status ===
                ResourceStatus.IDLE
              }
              useWindow={false}
              getScrollParent={() => scrollParentRef.current}
            >
              <SensorListView
                data={visibleData}
                columnSet={columnSet}
                sort={state.sort}
                onChangeSort={(column, direction) =>
                  dispatch({
                    type: InConfigurationActionTypes.IN_CONFIGURATION_SET_SORT,
                    column,
                    direction,
                  })
                }
                selectedSensorSerialNumbers={state.selectedSensorSerialNumbers}
                onChangeChecks={(serialNumbers, checked) =>
                  dispatch({
                    type:
                      InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_CHECKS,
                    serialNumbers,
                    checked,
                  })
                }
              />
            </ReactInfiniteScroller>
          </div>
          {state.resource.data.fetchingMoreSensors.status ===
            ResourceStatus.LOADING ? (
            <div className={styles.infiniteScrollLoading}>
              Loading more sensors...
            </div>
          ) : null}
          {state.resource.data.fetchingMoreSensors.status ===
            ResourceStatus.ERROR ? (
            <div
              className={classnames(styles.infiniteScrollLoading, styles.error)}
            >
              Error loading sensors:{' '}
              {state.resource.data.fetchingMoreSensors.error.toString()}
            </div>
          ) : null}
        </Fragment>
      );
  }
};

export const SensorListView: React.FunctionComponent<{
  data: Array<Sensor>;
  columnSet?: Array<string>;
  sort: Sort;
  onChangeSort?: (column: SortColumn, direction: SortDirection) => void;

  showDetails?: boolean;
  showChecks?: boolean;
  selectedSensorSerialNumbers?: Array<Sensor['serial_number']>;
  onChangeChecks?: (serialNumbers: Array<Sensor['serial_number']>, checked: boolean) => void;
}> = ({
  data,
  columnSet,

  sort,
  onChangeSort,

  showChecks = true,
  showDetails = true,
  selectedSensorSerialNumbers = [],
  onChangeChecks,
}) => {
    const dispatch = useRxDispatch();

    const sensorDetailHref = (sensor: Sensor) =>
      `#/${sensor.organization.id}/sensors/${sensor.serial_number}`;

    // Determine if all items are checked, because if so, highlight the checkbox in the listview header
    const allVisibleDataChecked =
      data.length > 0 &&
      data.every(item => selectedSensorSerialNumbers.includes(item.serial_number));

    const columnSetColumns = columnSet.map(name => {
      const key = name;

      // Dynamically generate all the columns for each annotation status
      if (key.startsWith('status_')) {
        for (let captureStatus in CAPTURE_STATUS_TO_PRETTY_NAME) {
          if (key === `status_${captureStatus}`) {
            const [name, desc] = CAPTURE_STATUS_TO_PRETTY_NAME[captureStatus];
            return (
              <ListViewColumn
                key={key}
                id={key}
                title={<ListViewTwoRowHeader header={name} subheader={desc} />}
                width={
                  {
                    [CaptureStatus.failed]: 200,
                    [CaptureStatus.frozen]: 210,
                    [CaptureStatus.created]: 170,
                    [CaptureStatus.mpi_pending_annotation]: 280,
                    [CaptureStatus.processing_annotations]: 280,
                    [CaptureStatus.qa_complete]: 200,
                    [CaptureStatus.preparing_to_post_qa_score]: 330,
                    [CaptureStatus.post_qa_score_complete]: 310,
                  }[captureStatus] || 230
                }
                template={(item: Sensor) =>
                  item.captures.by_status[captureStatus]
                }
              />
            );
          }
        }
      }

      switch (name) {
        case 'serial_number':
          return (
            <ListViewColumn
              key={key}
              id="serial_number"
              restrictSorting={true}
              title="Serial Number"
              width={150}
              template={(i: Sensor) => i.serial_number}
            />
          );
        case 'hardware_model':
          return (
            <ListViewColumn
              key={key}
              id="hardware_model"
              restrictSorting={true}
              title="HW Model"
              width={120}
              template={(i: Sensor) => {
                if (i.kind === 'openarea') {
                  return (
                    <div
                      className={classnames(styles.serialNumberKind, styles.openarea)}
                      data-test-id="in-configuration-kind-openarea"
                    >
                      <Icons.DeviceSide width={12} height={12} color="currentColor" />
                      <span>{i.hardware_model.toUpperCase()}</span>
                    </div>
                  );
                } else if (i.kind === 'entry') {
                  return (
                    <div
                      className={classnames(styles.serialNumberKind, styles.entry)}
                      data-test-id="in-configuration-kind-entry"
                    >
                      <Icons.DeviceTop width={12} height={12} color="currentColor" />
                      <span>{i.hardware_model.toUpperCase()}</span>
                    </div>
                  );
                } else {
                  return (
                    <div
                      className={styles.serialNumberKind}
                      data-test-id="in-configuration-kind-unknown"
                    >
                      {i.kind} ({i.hardware_model})
                    </div>
                  );
                }
              }}
            />
          );
        case 'doorway_name':
          return (
            <ListViewColumn
              key={key}
              id="doorway_name"
              title="Doorway Name"
              width={250}
              template={(i: Sensor) => i.doorway_name || MDASH}
            />
          );
        case 'space_name':
          return (
            <ListViewColumn
              key={key}
              id="space_name"
              restrictSorting={true}
              title="Space"
              width={250}
              template={(item: Sensor) =>
                item.spaces.length > 0 ? (
                  <Fragment>
                    <SpaceNameAncestry
                      id={item.spaces[0].id}
                      name={item.spaces[0].name}
                      ancestry={
                        // TODO - drop ancestry for direct list ordering
                        item.spaces[0].ancestry
                          ? item.spaces[0].ancestry.map(i => i.name)
                          : item.spaces.map(i => i.name)
                      }
                    />
                    {item.spaces.length > 1 ? 'and more...' : null}
                  </Fragment>
                ) : (
                  MDASH
                )
              }
            />
          );
        case 'assigned_team':
          return (
            <ListViewColumn
              key={key}
              id="assigned_team"
              title="Assigned Team"
              width={250}
              template={(item: Sensor) =>
                item.assigned_team ? (
                  ASSIGNABLE_TEAM_TO_PRETTY_NAME[item.assigned_team]
                ) : MDASH
              }
            />
          );
        case 'setup_step':
          return (
            <ListViewColumn
              key={key}
              id="current_state"
              restrictSorting={true}
              title="Setup Step"
              width={400}
              template={(item: Sensor) => (
                <SetupStepInfo
                  currentState={item.current_state}
                  currentStateMetadata={item.current_state_metadata}
                  onClickNoteAction={(noteId, noteAction) => (
                    dispatch({
                      type: InConfigurationActionTypes.IN_CONFIGURATION_CLICK_NOTE_ACTION,
                      serialNumber: item.serial_number,
                      noteId,
                      noteAction,
                    })
                  )}
                />
              )}
            />
          );
        case 'organization_name':
          return (
            <ListViewColumn
              key={key}
              id="organization_name"
              title="Organization"
              width={250}
              template={(item: Sensor) =>
                item.organization ? item.organization.name : MDASH
              }
            />
          );
        case 'first_online':
          return (
            <ListViewColumn
              key={key}
              id="first_online"
              restrictSorting={true}
              title="First Online"
              width={120}
              template={(i: Sensor) =>
                i.first_heartbeat ? formatDate(i.first_heartbeat) : MDASH
              }
            />
          );
        case 'capture_status':
          return (
            <ListViewColumn
              key={key}
              id="capture_status"
              restrictSorting={true}
              title="Capture Status"
              width={150}
              template={(sensor: Sensor) => (
                <DataCaptureStatusTag sensor={sensor} />
              )}
            />
          );

        case 'status':
          return (
            <ListViewColumn
              key={key}
              id="status"
              restrictSorting={true}
              title="Status"
              width={150}
              template={(i: Sensor) => (
                <StatusTag
                  status={i.current_status}
                />
              )}
            />
          );
        case 'os_version':
          return (
            <ListViewColumn
              key={key}
              id="os_version"
              restrictSorting={true}
              title="OS Version"
              width={110}
              template={(i: Sensor) => i.os_version ? (
                <div
                  style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Tooltip
                    contents={i.os_version}
                    target={<ColoredOSVersion short osVersion={i.os_version} />}
                  />
                </div>
              ) : MDASH}
            />
          );
        case 'annotation_events':
          return (
            <ListViewColumn
              key={key}
              id="annotation_events"
              restrictSorting={true}
              title={
                <div style={{ textAlign: 'left' }}>
                  Annotated
                  <br />
                  Events
                </div>
              }
              width={120}
              template={(item: Sensor) =>
                item.installation
                  ? <ColoredQaedEventCount value={item.installation.total_annotated_events} isMPI={false} />
                  : MDASH
              }
            />
          );
        case 'qaed_event_count':
          return (
            <ListViewColumn
              key={key}
              id="qaed_event_count"
              restrictSorting={true}
              title="QA'd Events"
              width={120}
              template={(item: Sensor) => (
                <ColoredQaedEventCount value={item.qaed_event_count} isMPI={false} />
              )}
            />
          );
        case 'accuracy':
          return (
            <ListViewColumn
              key={key}
              id="accuracy"
              title={
                <ListViewTwoRowHeader
                  header="Accuracy"
                  subheader="From the latest report"
                />
              }
              width={150}
              template={(item: Sensor) => (
                <ColoredPercent percent={item.latest_accuracy} />
              )}
            />
          );

        case 'spacer':
          return <ListViewColumnSpacer key={key} />;
        default:
          return null;
      }
    });

    const columns = [
      showChecks ? (
        <ListViewColumn
          id="Checkboxes"
          key="checkboxes"
          title={
            <Checkbox
              checked={allVisibleDataChecked}
              onChange={e => {
                if (onChangeChecks) {
                  onChangeChecks(
                    data.map(i => i.serial_number),
                    e.target.checked,
                  );
                }
              }}
              color={colorVariables.midnight}
            />
          }
          width={60}
          template={(item: Sensor) => (
            <div onMouseDown={e => e.stopPropagation()}>
              <Checkbox
                checked={selectedSensorSerialNumbers.includes(item.serial_number)}
                onChange={e => {
                  if (onChangeChecks) {
                    onChangeChecks([item.serial_number], e.target.checked);
                  }
                }}
                color={colorVariables.midnight}
                data-test-id="sensor-list-view-checkbox"
              />
            </div>
          )}
        />
      ) : null,

      ...columnSetColumns,

      showDetails ? (
        <ListViewColumn
          id="Details"
          key="details"
          title=" "
          width={100}
          template={(item: Sensor) => (
            <span style={{ display: 'flex', alignItems: 'center', height: 56 }}>
              <DetailsButton href={sensorDetailHref(item)} />
            </span>
          )}
        />
      ) : null,
    ];

    return (
      <ListView
        padOuterColumns
        data={data}
        sort={[sort]}
        headerHeight={48}
        rowHeight="auto"
        isRowSelected={item => selectedSensorSerialNumbers.includes(item.id)}
        onClickHeader={(column: string) => {
          // Don't allow fitlering these columns
          if (
            column === 'Checkboxes' ||
            column === 'Details' ||
            column === 'space_name'
          ) {
            return;
          }

          const lastSortDirection =
            column === sort.column ? sort.direction : 'none';
          const sortDirection: SortDirection = getNextSortDirection(
            lastSortDirection,
          );

          if (onChangeSort) {
            onChangeSort(column, sortDirection);
          }
        }}
        onMouseDownRow={item => {
          return singleClickDetector(() => {
            if (!showDetails) {
              return;
            }

            openInNewTab(sensorDetailHref(item));
          });
        }}
      >
        {columns}
      </ListView>
    );
  };

export default InConfiguration;
